import React, { Component } from "react";
import { Modal, Spinner } from "reactstrap";
import {
  listRoomBookingRequest,
  updatePaymentStatus,
  assignRoomNumber,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, SearchPill, Table, Chip, Btn,
} from "../../components/Console/ConsoleUI";
import PeriodFilter, { allTime, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";
import { HOTEL_TIME_ZONE } from "../../utils/period";
import { toast } from "react-toastify";
import { canWrite } from "../../services/PermissionService";

/**
 * Room bookings — console screen C2·06 ("Web Bookings").
 *
 * A reskin: the same paginated /roomBookingList call, the same detail modal and
 * the same "mark as paid" action as before. What changed is the shape — KPI
 * tiles over a quiet table, payment shown as a chip rather than a badge.
 *
 * The payment pills filter what is already on screen; they do not re-query, so
 * their counts are honestly labelled "on this page".
 *
 * Bookings now carry a `status` (§4.15). It is shown as its own chip; payment
 * stays its own chip, because a cash booking is "confirmed" before it is paid.
 * Check-in/out is run in the hotel's own software, so there are no stay
 * actions here.
 */

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase() || "?";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Loyalty points as "1,250 pts"; old bookings carry none. */
const pts = (n) => `${Number(n || 0).toLocaleString()} pts`;

/**
 * The booking's loyalty lines: "Points used" (with the discount they gave),
 * "Points returned" after a cancellation, and "Points earned".
 */
const pointsLines = (b, line) => {
  const used = Number(b.pointsRedeemed) || 0;
  const value = Number(b.loyaltyDiscount) || 0;
  const back = Number(b.pointsRefunded) || 0;
  const earned = Number(b.pointsEarned) || 0;
  return (
    <React.Fragment>
      {used > 0 && line("Points used", value > 0 ? `${pts(used)} (−${money(value)})` : pts(used))}
      {back > 0 && line("Points returned", `+${pts(back)}`)}
      {earned > 0 && line("Points earned", `+${pts(earned)}`)}
    </React.Fragment>
  );
};

/** Matches the server, which trims and cuts the room number at 20 characters. */
const ROOM_NUMBER_MAX = 20;

/**
 * RoomBooking.status (models/RoomBooking.js). "expired" is never stored: the
 * server derives it for an unpaid hold whose holdExpiresAt has passed.
 */
const STATUS = {
  pending_payment: { label: "Awaiting payment", tone: "warn" },
  confirmed: { label: "Confirmed", tone: "good" },
  checked_in: { label: "Checked in", tone: "good" },
  checked_out: { label: "Checked out", tone: "neutral" },
  cancelled: { label: "Cancelled", tone: "bad" },
  no_show: { label: "No-show", tone: "bad" },
  expired: { label: "Hold expired", tone: "neutral" },
};

/** Statuses after which nothing is owed: not counted as awaiting payment. */
const CLOSED = ["cancelled", "no_show", "expired"];

/**
 * The booking's status, or null on a legacy row the server sent without one.
 * A hold that has lapsed since the list was fetched reads as expired.
 */
const statusOf = (b) => {
  const s = b && b.status;
  if (!s) return null;
  if (s === "pending_payment" && b.holdExpiresAt && new Date(b.holdExpiresAt) <= new Date()) return "expired";
  return s;
};

/** "14:30" on the HOTEL's clock while an unpaid hold is still running, else null. */
const holdUntil = (b) => {
  if (!b || !b.holdExpiresAt) return null;
  const d = new Date(b.holdExpiresAt);
  if (isNaN(d.getTime()) || d <= new Date()) return null;
  return d.toLocaleTimeString("en-GB", {
    timeZone: HOTEL_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23", // not hour12:false, which can print "24:00" at midnight
  });
};

/** Paid is the payment flag alone: status "confirmed" includes unpaid cash bookings. */
const isPaid = (b) => Boolean(b.isPaymentSuccess);

/**
 * Money still owed. Legacy rows (no status): every unpaid row, as before.
 * Otherwise a cancelled, no-show or lapsed booking is no longer awaiting it.
 */
const isAwaitingPayment = (b) => {
  if (isPaid(b)) return false;
  const s = statusOf(b);
  return !s || !CLOSED.includes(s);
};

/** The cancellation itself is handled on the Room cancellations page. */
const hasCancelRequest = (b) => b.isCancellRequest === true && statusOf(b) !== "cancelled";

/** Guest initials disc, the design's row anchor. */
const Avatar = ({ name }) => (
  <span
    style={{
      width: 34, height: 34, borderRadius: 17, flex: "0 0 34px",
      background: C.tint, color: C.brown, display: "grid", placeItems: "center",
      fontSize: 13, fontWeight: 700,
    }}
  >
    {initials(name)}
  </span>
);

/** Numbered pager, matching the design's 34px rounded squares. */
const Pager = ({ page, pages, onGo }) => {
  if (pages <= 1) return null;
  const box = (extra) => ({
    width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center",
    fontSize: 14, cursor: "pointer", border: `1px solid ${C.border}`,
    background: "#fff", color: C.slate, ...extra,
  });
  const numbers = Array.from({ length: Math.min(5, pages) }, (_, i) => {
    if (pages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= pages - 2) return pages - 4 + i;
    return page - 2 + i;
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        onClick={() => page > 1 && onGo(page - 1)}
        style={box({ color: page > 1 ? C.slate : "#bfae99", cursor: page > 1 ? "pointer" : "default" })}
      >
        <span className="ms" style={{ fontSize: 19 }}>chevron_left</span>
      </span>
      {numbers.map((n) => (
        <span
          key={n}
          onClick={() => onGo(n)}
          style={box(n === page ? { background: C.brown, color: "#fff", border: "none" } : {})}
        >
          {n}
        </span>
      ))}
      <span
        onClick={() => page < pages && onGo(page + 1)}
        style={box({ color: page < pages ? C.slate : "#bfae99", cursor: page < pages ? "pointer" : "default" })}
      >
        <span className="ms" style={{ fontSize: 19 }}>chevron_right</span>
      </span>
    </div>
  );
};

class Booking extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      limit: 15,
      search: "",
      // Detail modal
      showDetailModal: false,
      selectedBooking: null,
      // Front-desk room number being edited in the detail modal.
      roomNumberDraft: "",
      savingRoomNumber: false,
      // View-only filter over the rows already fetched.
      paymentFilter: "all",
      // Which bookings the server lists ("" = placed and not cancelled, its default).
      listStatus: "",
      period: allTime(),
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Room Bookings", link: "#" },
      ],
    };
  }

  handlePeriodChange = (period) => {
    this.setState({ period }, () => this.getAllBookings(1));
  };

  componentDidMount() {
    this.getAllBookings();
  }

  getAllBookings = (page = this.state.currentPage, search = this.state.search) => {
    const { limit } = this.state;
    this.setState({ isLoaded: false });

    const { listStatus } = this.state;
    listRoomBookingRequest(page, limit, search, {
      ...periodToParams(this.state.period),
      ...(listStatus ? { status: listStatus } : {}),
    })
      .then((result) => {
        const pagination = result?.pagination || {};
        this.setState({
          isLoaded: true,
          items: result?.data || [],
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalCount: pagination.totalCount || 0,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ isLoaded: true, items: [] });
      });
  };

  // View booking detail
  viewBookingDetail = (booking) => {
    this.setState({
      showDetailModal: true,
      selectedBooking: booking,
      roomNumberDraft: booking.assignedRoomNumber || "",
    });
  };

  closeDetailModal = () => {
    this.setState({
      showDetailModal: false,
      selectedBooking: null,
      roomNumberDraft: "",
    });
  };

  // Assign, change or clear (empty string) the physical room number.
  saveRoomNumber = () => {
    const { selectedBooking, roomNumberDraft } = this.state;
    if (!selectedBooking) return;
    const value = roomNumberDraft.trim().slice(0, ROOM_NUMBER_MAX);
    this.setState({ savingRoomNumber: true });
    assignRoomNumber(selectedBooking._id, value)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(response.message);
          // Patched in place from the saved booking — no need to reload the page.
          const saved = (response.data && response.data.assignedRoomNumber) || "";
          const patch = (b) => (b._id === selectedBooking._id ? { ...b, assignedRoomNumber: saved } : b);
          this.setState((s) => ({
            items: s.items.map(patch),
            selectedBooking: s.selectedBooking ? patch(s.selectedBooking) : s.selectedBooking,
            roomNumberDraft: saved,
          }));
        } else {
          toast.error((response && response.message) || "Could not save the room number");
        }
      })
      .catch(() => toast.error("Could not save the room number"))
      .finally(() => this.setState({ savingRoomNumber: false }));
  };

  // Update payment status
  StatusChangeRequest = (roomBookingId, isPaymentSuccess) => {
    updatePaymentStatus(roomBookingId, isPaymentSuccess)
      .then((response) => {
        if (response && response.status === 200 && response.success !== false) {
          toast.success(response.message);
          this.getAllBookings();
          this.closeDetailModal();
        } else {
          toast.error((response && response.message) || "Error updating payment status");
        }
      })
      .catch(() => toast.error("Error updating payment status"));
  };

  // Get payment badge. An unpaid booking that is closed (cancelled, no-show,
  // lapsed hold) owes nothing, so it is not shown as pending.
  getPaymentBadge = (booking) => {
    if (isPaid(booking)) return <Chip tone="good">Paid</Chip>;
    if (isAwaitingPayment(booking)) return <Chip tone="warn">Pending</Chip>;
    return <Chip>Not paid</Chip>;
  };

  // Booking status chip; null on a legacy row without a status.
  getStatusChip = (booking) => {
    const s = statusOf(booking);
    if (!s) return null;
    const meta = STATUS[s] || { label: s, tone: "neutral" };
    const until = s === "pending_payment" ? holdUntil(booking) : null;
    return (
      <Chip tone={meta.tone}>
        {meta.label}
        {until ? ` · hold until ${until}` : ""}
      </Chip>
    );
  };

  // "Cancellation requested" hint; the request is reviewed elsewhere.
  getCancelRequestChip = (booking) =>
    hasCancelRequest(booking) ? (
      <Chip tone="bad">
        <span className="ms" style={{ fontSize: 15 }}>event_busy</span>
        Cancellation requested
      </Chip>
    ) : null;

  // Get payment method label
  getPaymentMethodLabel = (type) => {
    return type === 1 ? "Cash" : "Online";
  };

  /** Rows after the on-page payment filter. */
  visibleRows() {
    const { items, paymentFilter } = this.state;
    if (paymentFilter === "paid") return items.filter(isPaid);
    if (paymentFilter === "pending") return items.filter(isAwaitingPayment);
    return items;
  }

  columns() {
    return [
      {
        key: "createdAt",
        label: "Booked on",
        width: 150,
        render: (b) => <span style={{ color: C.taupe }}>{b.createdAt}</span>,
      },
      {
        key: "guest",
        label: "Guest",
        render: (b) => {
          const name = b.guestName || b.user || "N/A";
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <Avatar name={name} />
              <div>
                <div style={{ fontSize: 15, color: C.ink }}>{name}</div>
                <div style={{ fontSize: 12, color: C.taupeLight }}>
                  {b.bookingNumber ? `${b.bookingNumber} · ` : ""}
                  {b.guestMobile || b.mobile || "—"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: "room",
        label: "Room",
        render: (b) => (
          <div>
            <div style={{ fontSize: 15, color: C.ink }}>{b.roomName || "N/A"}</div>
            <div style={{ fontSize: 12, color: C.taupeLight }}>
              {b.roomType || "—"}
              {b.rooms > 1 ? ` · ${b.rooms} rooms` : ""}
              {b.assignedRoomNumber ? ` · Room ${b.assignedRoomNumber}` : ""}
            </div>
          </div>
        ),
      },
      {
        key: "startDate",
        label: "Check-in",
        width: 120,
        render: (b) => <span style={{ color: C.ink }}>{b.startDate}</span>,
      },
      {
        key: "endDate",
        label: "Check-out",
        width: 120,
        render: (b) => <span style={{ color: C.ink }}>{b.endDate}</span>,
      },
      {
        key: "status",
        label: "Status",
        width: 170,
        render: (b) => {
          const status = this.getStatusChip(b);
          const request = this.getCancelRequestChip(b);
          if (!status && !request) return <span style={{ color: C.taupeLight }}>—</span>;
          return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 5 }}>
              {status}
              {request}
            </div>
          );
        },
      },
      {
        key: "amount",
        label: "Amount",
        width: 110,
        render: (b) => (
          <span style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>
            {money(b.finalOrderAmount || b.total || 0)}
          </span>
        ),
      },
      {
        key: "payment",
        label: "Payment",
        width: 120,
        render: (b) => this.getPaymentBadge(b),
      },
      {
        key: "action",
        label: "",
        width: 130,
        render: (b) => (
          <div onClick={(e) => e.stopPropagation()}>
            {!b.isPaymentSuccess && b.paymentMethodType === 1 ? (
              // Settling a cash booking is a write. A view-only account still
              // needs to see that it is outstanding, so the state is reported
              // either way and only the action is withheld.
              canWrite() ? (
                <Btn
                  kind="quiet"
                  style={{ height: 32, padding: "0 14px", fontSize: 13 }}
                  onClick={() => this.StatusChangeRequest(b._id, true)}
                >
                  Mark paid
                </Btn>
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.taupe, fontSize: 13 }}>
                  <span className="ms" style={{ fontSize: 18 }}>schedule</span>
                  Awaiting payment
                </span>
              )
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.taupeLight, fontSize: 13 }}>
                <span className="ms" style={{ fontSize: 18 }}>done_all</span>
                Complete
              </span>
            )}
          </div>
        ),
      },
    ];
  }

  /** Front-desk room number: an input + Save for writers, the value alone otherwise. */
  renderRoomNumber(booking) {
    const { roomNumberDraft, savingRoomNumber } = this.state;
    const current = booking.assignedRoomNumber || "";
    if (!canWrite()) {
      // Nothing to show a view-only account until a room is assigned.
      if (!current) return null;
      return (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 14, color: C.slate, marginTop: 9 }}>
          <span>Assigned room</span>
          <span style={{ color: C.ink, textAlign: "right" }}>{current}</span>
        </div>
      );
    }
    const unchanged = roomNumberDraft.trim() === current;
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 14, color: C.slate }}>Assigned room</div>
        <div style={{ marginTop: 7, display: "flex", gap: 10, alignItems: "center" }}>
          <input
            type="text"
            value={roomNumberDraft}
            maxLength={ROOM_NUMBER_MAX}
            placeholder="e.g. 204 — leave empty to clear"
            onChange={(e) => this.setState({ roomNumberDraft: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !unchanged && !savingRoomNumber) this.saveRoomNumber();
            }}
            style={{
              flex: 1, minWidth: 0, height: 40, padding: "0 13px", borderRadius: 12,
              border: "1px solid #e6d8c4", background: "#fff", color: C.ink, fontSize: 15,
              fontFamily: "inherit", outline: "none",
            }}
          />
          <Btn
            kind="quiet"
            style={{ height: 40, padding: "0 16px", fontSize: 14 }}
            disabled={unchanged || savingRoomNumber}
            onClick={this.saveRoomNumber}
          >
            {savingRoomNumber ? "Saving…" : "Save"}
          </Btn>
        </div>
      </div>
    );
  }

  renderDetailModal() {
    const { selectedBooking } = this.state;
    const line = (label, value) => (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 14, color: C.slate, marginTop: 9 }}>
        <span>{label}</span>
        <span style={{ color: C.ink, textAlign: "right" }}>{value || "N/A"}</span>
      </div>
    );

    return (
      <Modal
        isOpen={this.state.showDetailModal}
        toggle={this.closeDetailModal}
        size="lg"
        scrollable
        contentClassName="bg-transparent border-0"
          className="con-modal"
        >
        {selectedBooking && (
          <div style={{ background: "#fdf9f3", borderRadius: 22, padding: 32 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <span
                className="ms"
                style={{ fontSize: 26, color: C.brown, background: C.iconTint, padding: 13, borderRadius: 16 }}
              >
                hotel
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 500, color: C.ink, letterSpacing: "-.3px" }}>
                  {selectedBooking.bookingNumber ||
                    `#${(selectedBooking._id || "").slice(-6).toUpperCase()}`}
                </div>
                <div style={{ marginTop: 6, fontSize: 15, color: C.slate }}>
                  Booked on {selectedBooking.createdAt}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 6 }}>
                {this.getStatusChip(selectedBooking)}
                {this.getPaymentBadge(selectedBooking)}
              </div>
              <span
                className="ms"
                onClick={this.closeDetailModal}
                style={{ fontSize: 22, color: C.taupeLight, cursor: "pointer" }}
              >
                close
              </span>
            </div>

            {hasCancelRequest(selectedBooking) && (
              <div
                style={{
                  marginTop: 18, padding: "12px 16px", borderRadius: 14,
                  background: "rgba(192,32,43,.08)", display: "flex", alignItems: "center", gap: 10,
                }}
              >
                {this.getCancelRequestChip(selectedBooking)}
                <span style={{ fontSize: 14, color: C.slate }}>
                  The guest asked to cancel. Approve or decline it under Room cancellations.
                </span>
              </div>
            )}

            <div style={{ marginTop: 22, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
              <div style={{ fontSize: 13, color: C.taupe, letterSpacing: ".6px" }}>GUEST</div>
              {line("Name", selectedBooking.guestName || selectedBooking.user)}
              {line("Email", selectedBooking.guestEmail || selectedBooking.email)}
              {line("Phone", selectedBooking.guestMobile || selectedBooking.mobile)}
            </div>

            <div style={{ marginTop: 14, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
              <div style={{ fontSize: 13, color: C.taupe, letterSpacing: ".6px" }}>ROOM &amp; STAY</div>
              {line("Room", selectedBooking.roomName)}
              {line("Room type", selectedBooking.roomType)}
              {line("No. of rooms", selectedBooking.rooms || 1)}
              {line("Nights", selectedBooking.nights)}
              {this.renderRoomNumber(selectedBooking)}
            </div>

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                <div style={{ fontSize: 12, color: C.taupeLight }}>Check-in</div>
                <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>{selectedBooking.startDate}</div>
              </div>
              <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                <div style={{ fontSize: 12, color: C.taupeLight }}>Check-out</div>
                <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>{selectedBooking.endDate}</div>
              </div>
            </div>

            {selectedBooking.specialRequests && (
              <div style={{ marginTop: 14, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
                <div style={{ fontSize: 13, color: C.taupe, letterSpacing: ".6px" }}>SPECIAL REQUESTS</div>
                <div style={{ marginTop: 7, fontSize: 15, color: C.ink, fontStyle: "italic" }}>
                  “{selectedBooking.specialRequests}”
                </div>
              </div>
            )}

            <div style={{ marginTop: 14, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
              {selectedBooking.couponCode && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
                  <span>Coupon ({selectedBooking.couponCode})</span>
                  <span style={{ color: "#2f7d33" }}>
                    −{money(selectedBooking.couponDiscount || 0)}
                  </span>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate, marginTop: 9 }}>
                <span>Payment method</span>
                <span style={{ color: C.ink }}>
                  {this.getPaymentMethodLabel(selectedBooking.paymentMethodType)}
                </span>
              </div>
              {pointsLines(selectedBooking, line)}
              <div
                style={{
                  marginTop: 12, paddingTop: 12, borderTop: "1px solid #e6d8c4",
                  display: "flex", justifyContent: "space-between",
                  fontSize: 16, fontWeight: 500, color: C.ink,
                }}
              >
                <span>Total</span>
                <span>{money(selectedBooking.finalOrderAmount || selectedBooking.total || 0)}</span>
              </div>
            </div>

            <div style={{ marginTop: 26, display: "flex", gap: 12, justifyContent: "flex-end" }}>
              {!selectedBooking.isPaymentSuccess && selectedBooking.paymentMethodType === 1 && canWrite() && (
                <Btn onClick={() => this.StatusChangeRequest(selectedBooking._id, true)} icon="check">
                  Mark as paid
                </Btn>
              )}
              <Btn kind="quiet" onClick={this.closeDetailModal}>
                Close
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    const { items, isLoaded, currentPage, totalPages, totalCount, limit, search, paymentFilter } = this.state;
    const rows = this.visibleRows();
    const paid = items.filter(isPaid).length;
    // Not items.length - paid: a closed booking (cancelled, no-show, lapsed
    // hold) is neither paid nor awaiting payment.
    const pending = items.filter(isAwaitingPayment).length;
    const pageValue = items.reduce((s, b) => s + Number(b.finalOrderAmount || b.total || 0), 0);
    const unpaidValue = items
      .filter(isAwaitingPayment)
      .reduce((s, b) => s + Number(b.finalOrderAmount || b.total || 0), 0);

    const tabs = [
      { value: "all", label: `All · ${items.length}` },
      { value: "paid", label: `Paid · ${paid}` },
      { value: "pending", label: `Awaiting payment · ${pending}` },
    ];

    return (
      <ConsolePage
        title="Bookings"
        subtitle={`Rooms · ${totalCount} booking${totalCount === 1 ? "" : "s"}${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Guest name, email or phone"
              width={260}
            />
            <Btn icon="search" onClick={() => this.getAllBookings(1, search)}>
              Search
            </Btn>
            {search ? (
              <Btn
                kind="quiet"
                onClick={() => this.setState({ search: "" }, () => this.getAllBookings(1, ""))}
              >
                Clear
              </Btn>
            ) : null}
          </React.Fragment>
        }
      >
        {this.renderDetailModal()}

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <PeriodFilter value={this.state.period} onChange={this.handlePeriodChange} />
          <StatRow>
            <StatCard icon="event_available" value={totalCount} label={this.state.period.mode === "all" ? "Bookings in total" : `Bookings · ${periodLabel(this.state.period)}`} />
            <StatCard icon="payments" value={paid} label="Paid, on this page" />
            <StatCard icon="pending_actions" value={pending} label="Awaiting payment, on this page" />
            <StatCard icon="account_balance_wallet" value={money(unpaidValue)} label={`Unpaid of ${money(pageValue)} on this page`} />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "14px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              {tabs.map((t) => (
                <span
                  key={t.value}
                  onClick={() => this.setState({ paymentFilter: t.value })}
                  style={{
                    padding: "8px 15px", borderRadius: 17, fontSize: 14, cursor: "pointer",
                    background: paymentFilter === t.value ? C.brown : "transparent",
                    color: paymentFilter === t.value ? "#fff" : C.taupe,
                  }}
                >
                  {t.label}
                </span>
              ))}
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: C.taupe }}>
                <span>Showing</span>
                <select
                  aria-label="Which bookings"
                  value={this.state.listStatus}
                  onChange={(e) => this.setState({ listStatus: e.target.value, paymentFilter: "all" }, () => this.getAllBookings(1))}
                  style={{
                    height: 34, borderRadius: 10, border: `1px solid ${C.border}`,
                    background: "#fff", color: C.brownDeep, fontSize: 14, padding: "0 8px",
                  }}
                >
                  <option value="">Booked</option>
                  <option value="pending">Unpaid holds</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="all">Everything</option>
                </select>
                <span>Rows</span>
                <select
                  value={limit}
                  onChange={(e) =>
                    this.setState({ limit: parseInt(e.target.value) }, () => this.getAllBookings(1))
                  }
                  style={{
                    height: 34, borderRadius: 10, border: `1px solid ${C.border}`,
                    background: "#fff", color: C.brownDeep, fontSize: 14, padding: "0 8px",
                  }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div style={{ padding: "0 10px" }}>
              {!isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  <Spinner style={{ color: C.brown }} />
                  <div style={{ marginTop: 10 }}>Loading bookings…</div>
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={rows}
                  empty="No bookings match this view"
                  onRowClick={this.viewBookingDetail}
                />
              )}
            </div>

            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 22px", borderTop: `1px solid ${C.panel}`,
              }}
            >
              <div style={{ fontSize: 14, color: C.taupeLight }}>
                Page {currentPage} of {totalPages} · {totalCount} booking
                {totalCount === 1 ? "" : "s"}
              </div>
              <Pager page={currentPage} pages={totalPages} onGo={(p) => this.getAllBookings(p)} />
            </div>
          </Panel>
        </div>
      </ConsolePage>
    );
  }
}

export default Booking;
